import { useState, type ReactNode } from 'react';
import { HeaderShell } from '../components/Header.tsx';
import type { LeaderboardEntry } from '@cruz/shared';

/** Pieces shared by the challenge screens, in the chatbot's style. */

export const primaryButton =
  'inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-xl bg-purple px-5 py-3 ' +
  'text-lg font-semibold text-cream transition duration-150 hover:bg-purple-500 ' +
  'active:scale-[0.99] active:bg-purple-700 disabled:opacity-60 ' +
  'dark:bg-gold-300 dark:text-night dark:hover:bg-gold';

export const secondaryButton =
  'inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-xl border ' +
  'border-purple/25 bg-paper px-5 py-3 text-lg text-purple transition duration-150 ' +
  'hover:border-purple/50 hover:bg-white active:scale-[0.99] active:bg-cream-200/60 ' +
  'dark:border-cream/20 dark:bg-night-800 dark:text-cream dark:hover:border-gold/60';

export const inputClass =
  'min-h-12 w-full rounded-xl border border-purple/25 bg-paper px-4 py-2.5 text-lg text-ink ' +
  'transition-colors placeholder:text-ink-soft/70 focus:border-gold-700 focus:outline-none ' +
  'dark:border-cream/20 dark:bg-night-700 dark:text-cream dark:placeholder:text-cream/50 ' +
  'dark:focus:border-gold';

export function ChallengeLayout({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-dvh flex-col">
      <HeaderShell title="Reto cofrade" subtitle="Cruz de IA · Semana Santa" titleAs="p">
        <a
          href="/"
          className="rounded-full px-3 py-2 text-sm font-medium text-cream/90 transition-colors hover:bg-white/10 hover:text-cream"
        >
          Chat
        </a>
      </HeaderShell>
      <main className="animate-rise mx-auto w-full max-w-2xl flex-1 px-4 py-5 pb-[calc(1.25rem+env(safe-area-inset-bottom))]">
        {children}
      </main>
    </div>
  );
}

export function Notice({
  tone = 'info',
  children,
}: {
  tone?: 'info' | 'error' | 'warn';
  children: ReactNode;
}) {
  const colors = {
    info: 'border-purple/20 bg-paper dark:border-cream/15 dark:bg-night-800',
    warn: 'border-gold/70 bg-cream-200 dark:border-gold/40 dark:bg-gold/10',
    error: 'border-burgundy/40 bg-burgundy-300/25 dark:border-burgundy-300/40 dark:bg-burgundy/20',
  }[tone];
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={`rounded-xl border px-4 py-3 text-base ${colors}`}
    >
      {children}
    </div>
  );
}

/** "12,3 s" */
export const seconds = (ms: number) => `${(ms / 1000).toFixed(1).replace('.', ',')} s`;

export function Leaderboard({
  entries,
  playing,
  me,
}: {
  entries: LeaderboardEntry[];
  playing: string[];
  me?: string | null;
}) {
  if (entries.length === 0 && playing.length === 0) {
    return <p className="text-ink-soft dark:text-cream/75">Aún no ha jugado nadie.</p>;
  }
  return (
    <div>
      {entries.length > 0 && (
        <ol className="divide-y divide-purple/10 overflow-hidden rounded-xl border border-purple/15 bg-paper dark:divide-cream/10 dark:border-cream/15 dark:bg-night-800">
          {entries.map((e) => (
            <li
              key={e.nickname}
              className={`flex items-center gap-3 px-4 py-3 ${e.nickname === me ? 'bg-cream-200 dark:bg-gold/10' : ''}`}
            >
              <span className="tabular w-7 text-center font-serif text-2xl font-semibold text-purple dark:text-gold-300">
                {e.rank}
              </span>
              <span className="min-w-0 flex-1 truncate text-lg">
                {e.nickname}
                {e.is_creator && (
                  <span className="ml-2 text-sm text-ink-soft dark:text-cream/70">(creador)</span>
                )}
              </span>
              <span className="text-right">
                <span className="block text-lg font-semibold">
                  {e.correct} {e.correct === 1 ? 'acierto' : 'aciertos'}
                </span>
                <span className="tabular block text-sm text-ink-soft dark:text-cream/70">
                  {seconds(e.total_ms)}
                </span>
              </span>
            </li>
          ))}
        </ol>
      )}
      {playing.length > 0 && (
        <p className="mt-2 text-sm text-ink-soft dark:text-cream/75">
          Jugando ahora: {playing.join(', ')}
        </p>
      )}
    </div>
  );
}

const SHARE_TEXT = '¡Te reto a demostrar cuánto sabes de la Semana Santa de Sevilla!';

export function ShareButtons({ code, creator }: { code: string; creator: string }) {
  const [copied, setCopied] = useState<'ok' | 'failed' | null>(null);
  const url = `${location.origin}/reto/${code}`;
  const text = `${SHARE_TEXT} ${creator} te desafía en Cruz de IA:`;
  const canShare = typeof navigator.share === 'function';
  const share = () => {
    navigator.share({ title: 'Reto cofrade', text, url }).catch(() => {
      // Cancelled by the user: nothing to do
    });
  };
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied('ok');
    } catch {
      setCopied('failed');
    }
  };
  return (
    <div className="space-y-3">
      {canShare && (
        <button type="button" className={primaryButton} onClick={share}>
          Compartir el reto
        </button>
      )}
      <a
        className={canShare ? secondaryButton : primaryButton}
        href={`https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`}
        target="_blank"
        rel="noopener noreferrer"
      >
        Enviar por WhatsApp
      </a>
      <button type="button" className={secondaryButton} onClick={() => void copy()}>
        {copied === 'ok' ? '¡Enlace copiado!' : 'Copiar enlace'}
      </button>
      {copied === 'failed' && (
        <p className="text-base">
          Copia este enlace: <span className="break-all font-semibold select-all">{url}</span>
        </p>
      )}
    </div>
  );
}

/** Highlights the NO of negative questions, as the rules ask. */
export function Prompt({ text }: { text: string }) {
  const parts = text.split(/\b(NO)\b/);
  return (
    <>
      {parts.map((p, i) =>
        p === 'NO' ? (
          <strong
            key={i}
            className="rounded bg-burgundy px-1 text-cream dark:bg-burgundy-300 dark:text-night"
          >
            NO
          </strong>
        ) : (
          <span key={i}>{p}</span>
        ),
      )}
    </>
  );
}
